import { useState } from 'react';
import { AlertTriangle, Check, X, Eye } from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/input';
import type { FraudAlert, FraudAlertLevel, FraudAlertStatus } from '../types/admin-partner';

interface FraudAlertListProps {
  alerts: FraudAlert[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onReview: (id: string, status: 'REVIEWED' | 'DISMISSED', notes?: string) => void;
  isReviewing?: boolean;
}

const levelVariants: Record<FraudAlertLevel, 'success' | 'warning' | 'danger'> = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
};

const levelLabels: Record<FraudAlertLevel, string> = {
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
  CRITICAL: 'Crítico',
};

const statusVariants: Record<FraudAlertStatus, 'warning' | 'success' | 'outline' | 'danger'> = {
  PENDING: 'warning',
  REVIEWED: 'success',
  DISMISSED: 'outline',
  APPROVED: 'success',
};

const statusLabels: Record<FraudAlertStatus, string> = {
  PENDING: 'Pendente',
  REVIEWED: 'Revisado',
  DISMISSED: 'Descartado',
  APPROVED: 'Aprovado',
};

export function FraudAlertList({
  alerts,
  isLoading,
  total,
  page,
  pageSize,
  onPageChange,
  onReview,
  isReviewing,
}: FraudAlertListProps) {
  const [selectedAlert, setSelectedAlert] = useState<FraudAlert | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const handleReview = (status: 'REVIEWED' | 'DISMISSED') => {
    if (selectedAlert) {
      onReview(selectedAlert.id, status, reviewNotes);
      setSelectedAlert(null);
      setReviewNotes('');
    }
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando alertas..." />;
  }

  if (alerts.length === 0) {
    return (
      <EmptyState
        title="Nenhum alerta de fraude encontrado"
        description="Não há alertas de fraude cadastrados."
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nível</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Parceiro</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Regra</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Score</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Data</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {alerts.map((alert) => (
              <tr
                key={alert.id}
                className={`hover:bg-muted/30 ${
                  alert.level === 'HIGH' || alert.level === 'CRITICAL'
                    ? 'bg-destructive/5'
                    : ''
                }`}
              >
                <td className="px-4 py-3">
                  <Badge variant={levelVariants[alert.level]}>
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {levelLabels[alert.level]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm font-medium">{alert.partnerName}</td>
                <td className="px-4 py-3 text-sm">
                  <p className="font-medium">{alert.rule}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {alert.ruleDescription}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          alert.score >= 0.8
                            ? 'bg-destructive'
                            : alert.score >= 0.5
                            ? 'bg-warning'
                            : 'bg-success'
                        }`}
                        style={{ width: `${alert.score * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{(alert.score * 100).toFixed(0)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariants[alert.status]}>
                    {statusLabels[alert.status]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(alert.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedAlert(alert)}
                      title="Revisar"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {alert.status === 'PENDING' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAlert(alert);
                            handleReview('REVIEWED');
                          }}
                          disabled={isReviewing}
                          title="Aprovar"
                          className="text-success hover:text-success"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAlert(alert);
                            handleReview('DISMISSED');
                          }}
                          disabled={isReviewing}
                          title="Descartar"
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <PaginationInfo
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revisar Alerta de Fraude</DialogTitle>
            <DialogDescription>
              Analise os detalhes do alerta e tome uma decisão.
            </DialogDescription>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Parceiro</p>
                  <p className="font-medium">{selectedAlert.partnerName}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Nível</p>
                  <Badge variant={levelVariants[selectedAlert.level]}>
                    {levelLabels[selectedAlert.level]}
                  </Badge>
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2">Regra: {selectedAlert.rule}</p>
                <p className="text-sm text-muted-foreground">{selectedAlert.ruleDescription}</p>
              </div>

              <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2">Score de Risco</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        selectedAlert.score >= 0.8
                          ? 'bg-destructive'
                          : selectedAlert.score >= 0.5
                          ? 'bg-warning'
                          : 'bg-success'
                      }`}
                      style={{ width: `${selectedAlert.score * 100}%` }}
                    />
                  </div>
                  <span className="font-bold">{(selectedAlert.score * 100).toFixed(0)}%</span>
                </div>
              </div>

              {selectedAlert.metadata && Object.keys(selectedAlert.metadata).length > 0 && (
                <div className="p-4 border rounded-lg">
                  <p className="text-sm font-medium mb-2">Metadados</p>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                    {JSON.stringify(selectedAlert.metadata, null, 2)}
                  </pre>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Notas da Revisão</label>
                <Input
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Adicione observações sobre sua decisão..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedAlert(null);
                setReviewNotes('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleReview('DISMISSED')}
              disabled={isReviewing}
            >
              Descartar
            </Button>
            <Button
              variant="primary"
              onClick={() => handleReview('REVIEWED')}
              disabled={isReviewing}
              isLoading={isReviewing}
            >
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
